import React, { useState, useEffect } from 'react';
import { OutlineButton, PrimaryButton } from '../../../components/Buttons';
import { ChevronDown, ArrowLeft, Edit2, Plus, Trash2, Save } from 'lucide-react';

const ConditionBuilder = ({ setConditionString, initialConditions = [], onBack }) => {
    const [localConditions, setLocalConditions] = useState(initialConditions);
    const [isEditing, setIsEditing] = useState(!initialConditions?.length);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCondition, setNewCondition] = useState({
        field: '',
        operator: '',
        value1: '',
        value2: '',
        condition: 'AND',
    });

    // Update localConditions if initialConditions changes
    useEffect(() => {
        if (initialConditions && initialConditions.length > 0) {
            setLocalConditions(initialConditions);
        }
    }, [initialConditions]);

    const [formattedValue1, setFormattedValue1] = useState('');
    const [formattedValue2, setFormattedValue2] = useState('');

    const fields = [
        { label: "Project Revenue", value: "revenue" },
        { label: "Project Status", value: "completion_percent" },
        { label: "Inventory Unsold", value: "unsold_units" },
        { label: "Inventory Sold", value: "sold_units" },
        { label: "Timeline", value: "timeline_end" },
    ];

    const operators = [
        "Greater Than or Equal To",
        "Less Than or Equal To",
        "Between",
        "Equal To",
        "Greater Than",
        "Less Than",
    ];

    const conditionsOptions = ["AND", "OR", "None"];

    // Update conditions and conditionString when conditions change
    useEffect(() => {
        const conditionString = buildConditionString();
        setConditionString(conditionString, localConditions);
    }, [localConditions, setConditionString]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;

        // If changing the field to timeline_end, set operator to "Equal To"
        if (name === "field" && value === "timeline_end") {
            setNewCondition(prev => ({ 
                ...prev, 
                [name]: value,
                operator: "Equal To" 
            }));
            return;
        }

        if (newCondition.field === "completion_percent") {
            formattedValue = value.replace(/[^0-9]/g, '') + '%';
        } else if (newCondition.field === "revenue") {
            formattedValue = '₹' + value.replace(/[^0-9]/g, '');
        } else if (["unsold_units", "sold_units"].includes(newCondition.field)) {
            formattedValue = '#' + value.replace(/[^0-9]/g, '');
        }

        if (name === "value1") {
            setFormattedValue1(formattedValue);
        } else if (name === "value2") {
            setFormattedValue2(formattedValue);
        }

        setNewCondition(prev => ({ 
            ...prev, 
            [name]: name === "value1" || name === "value2" ? 
                (newCondition.field === "timeline_end" ? value : value.replace(/[^0-9]/g, '')) 
                : value 
        }));
    };

    const handleExistingConditionChange = (index, e) => {
        const { name, value } = e.target;
        const updatedConditions = [...localConditions];
        let formattedValue = value;

        // If changing the field to timeline_end, set operator to "Equal To"
        if (name === "field" && value === "timeline_end") {
            updatedConditions[index] = {
                ...updatedConditions[index],
                [name]: value,
                operator: "Equal To"
            };
            setLocalConditions(updatedConditions);
            return;
        }

        if (updatedConditions[index].field === "completion_percent") {
            formattedValue = value.replace(/[^0-9]/g, '') + '%';
        } else if (updatedConditions[index].field === "revenue") {
            formattedValue = '₹' + value.replace(/[^0-9]/g, '');
        } else if (["unsold_units", "sold_units"].includes(updatedConditions[index].field)) {
            formattedValue = '#' + value.replace(/[^0-9]/g, '');
        }

        updatedConditions[index] = {
            ...updatedConditions[index],
            [name]: name === "value1" || name === "value2" ? 
                (updatedConditions[index].field === "timeline_end" ? value : value.replace(/[^0-9]/g, ''))
                : value
        };
        setLocalConditions(updatedConditions);
    };

    const addCondition = () => {
        if (!newCondition.field || !newCondition.operator || !newCondition.value1 ||
            (newCondition.operator === "Between" && !newCondition.value2)) {
            return;
        }
        setLocalConditions([...localConditions, newCondition]);
        setNewCondition({ field: '', operator: '', value1: '', value2: '', condition: 'AND' });
        setShowAddForm(false);
    };

    const removeCondition = (index) => {
        const updatedConditions = [...localConditions];
        updatedConditions.splice(index, 1);
        setLocalConditions(updatedConditions);
    };

   const buildConditionString = () => {
        if (!localConditions.length) return '';

        let conditionString = '';

        localConditions.forEach((condition, index) => {
            if (!condition.field || !condition.operator || !condition.value1 ||
                (condition.operator === "Between" && !condition.value2)) {
                console.warn(`Skipping condition ${index + 1} due to missing values.`);
                return;
            }

            if (index > 0 && condition.condition !== 'None') {
                conditionString += ` ${condition.condition} `;
            }

            const field = condition.field;
            const operator = condition.operator;

            if (operator === "Between") {
                let value1 = condition.value1;
                let value2 = condition.value2;

                if (field === "timeline_end") {
                    value1 = `'${value1}'`;
                    value2 = `'${value2}'`;
                } else {
                    const value1Num = Number(value1);
                    const value2Num = Number(value2);
                    if (isNaN(value1Num) || isNaN(value2Num)) {
                        console.warn(`Invalid number values in condition ${index + 1}. Skipping.`);
                        return;
                    }
                    value1 = value1Num;
                    value2 = value2Num;
                }
                conditionString += `(${field} >= ${value1} AND ${field} <= ${value2})`;
            } else {
                let opSymbol = '';
                switch (operator) {
                    case "Greater Than or Equal To":
                        opSymbol = '>=';
                        break;
                    case "Less Than or Equal To":
                        opSymbol = '<=';
                        break;
                    case "Equal To":
                        opSymbol = '=';
                        break;
                    case "Greater Than":
                        opSymbol = '>';
                        break;
                    case "Less Than":
                        opSymbol = '<';
                        break;
                    default:
                        console.warn(`Invalid operator in condition ${index + 1}. Skipping.`);
                        return;
                }

                let value = condition.value1;
                if (field === "timeline_end") {
                    value = `'${value}'`;
                } else {
                    const valueNum = Number(value);
                    if (isNaN(valueNum)) {
                        console.warn(`Invalid number value in condition ${index + 1}. Skipping.`);
                        return;
                    }
                    value = valueNum;
                }

                conditionString += `${field} ${opSymbol} ${value}`;
            }
        });
        console.log("created",conditionString);

        return conditionString;
    };

    const getInputType = (field) => {
        switch (field) {
            case "completion_percent":
                return "percentage";
            case "revenue":
                return "currency";
            case "unsold_units":
            case "sold_units":
                return "number";
            case "timeline_end":
                return "date";
            default:
                return "text";
        }
    };

    const getInputPlaceholder = (field) => {
        switch (field) {
            case "completion_percent":
                return "%";
            case "revenue":
                return "₹";
            case "unsold_units":
            case "sold_units":
                return "#";
            default:
                return "";
        }
    };

    const getFieldLabel = (value) => {
        const field = fields.find(f => f.value === value);
        return field ? field.label : value;
    };

    const getDisplayValue = (condition) => {
        let value = condition.value1;
        if (condition.field === "completion_percent") value += '%';
        if (condition.field === "revenue") value = '₹' + value;
        if (["unsold_units", "sold_units"].includes(condition.field)) value = '#' + value;
        return value;
    };

    const getOperatorSymbol = (operator) => {
        switch (operator) {
            case "Greater Than or Equal To": return "≥";
            case "Less Than or Equal To": return "≤";
            case "Equal To": return "=";
            case "Greater Than": return ">";
            case "Less Than": return "<";
            case "Between": return "between";
            default: return operator;
        }
    };

    const renderReadOnlyView = () => (
        <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold">Conditions</h2>
                <PrimaryButton onClick={() => setShowAddForm(true)}>
                    Add Condition
                </PrimaryButton>
            </div>
            {localConditions.length === 0 && (
                <div className="text-gray-500 text-sm mb-2">No conditions set</div>
            )}
            <div className="space-y-2">
                {localConditions.map((condition, index) => (
                    <div key={index} className="flex items-center bg-white border rounded-lg p-2">
                        <span className="text-gray-500 text-sm mr-2">{index + 1}.</span>
                        <span className="font-medium mr-2">{getFieldLabel(condition.field)}</span>
                        <span className="text-gray-600 mr-2">{getOperatorSymbol(condition.operator)}</span>
                        {condition.operator === "Between" ? (
                            <span>{getDisplayValue(condition)} and {getDisplayValue({ ...condition, value1: condition.value2 })}</span>
                        ) : (
                            <span>{getDisplayValue(condition)}</span>
                        )}
                        <button
                            className="ml-auto text-red-500 hover:text-red-700 p-1"
                            onClick={() => removeCondition(index)}
                            title="Remove"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}
            </div>
           
            {showAddForm && renderAddForm()}
        </div>
    );

    const renderAddForm = () => (
        <div className="mt-4 p-4 bg-flamingo-50 rounded-lg">
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="w-40">
                    <select
                        name="field"
                        className="w-full text-sm py-1.5 px-2 border rounded"
                        value={newCondition.field}
                        onChange={handleInputChange}
                    >
                        <option value="">Select Type</option>
                        {fields.map((field) => (
                            <option key={field.value} value={field.value}>{field.label}</option>
                        ))}
                    </select>
                </div>
                <div className="w-40">
                    <select
                        name="operator"
                        className="w-full text-sm py-1.5 px-2 border rounded"
                        value={newCondition.operator}
                        onChange={handleInputChange}
                    >
                        <option value="">Select Parameter</option>
                        {operators.map((operator) => (
                            <option key={operator} value={operator}>{operator}</option>
                        ))}
                    </select>
                </div>
                {newCondition.operator === "Between" ? (
                    <div className="flex items-center gap-2">
                        <input
                            type={getInputType(newCondition.field) === "date" ? "date" : "text"}
                            name="value1"
                            className="w-32 text-sm py-1.5 px-2 border rounded"
                            placeholder={getInputPlaceholder(newCondition.field)}
                            value={newCondition.value1}
                            onChange={handleInputChange}
                        />
                        <span className="text-sm text-gray-500">and</span>
                        <input
                            type={getInputType(newCondition.field) === "date" ? "date" : "text"}
                            name="value2"
                            className="w-32 text-sm py-1.5 px-2 border rounded"
                            placeholder={getInputPlaceholder(newCondition.field)}
                            value={newCondition.value2}
                            onChange={handleInputChange}
                        />
                    </div>
                ) : (
                    <input
                        type={getInputType(newCondition.field) === "date" ? "date" : "text"}
                        name="value1"
                        className="w-32 text-sm py-1.5 px-2 border rounded"
                        placeholder={getInputPlaceholder(newCondition.field)}
                        value={newCondition.value1}
                        onChange={handleInputChange}
                    />
                )}
                <PrimaryButton
                   className='w-20'
                    onClick={addCondition}
                    disabled={!newCondition.field || !newCondition.operator || !newCondition.value1 ||
                        (newCondition.operator === "Between" && !newCondition.value2)}
                >
                   Save
                </PrimaryButton>
                <button
                    className="ml-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded border border-gray-200"
                    onClick={() => setShowAddForm(false)}
                    type="button"
                >
                    Cancel
                </button>
            </div>
        </div>
    );

    return renderReadOnlyView();
};

export default ConditionBuilder;